/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Context Clues - Reading comprehension game
 *
 * SAT Grounding: Information and Ideas domain.
 * Tests Central Ideas, Details, and Inferences skills.
 * Shows passage excerpts with comprehension questions. Tracks streak.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  BookOpen,
  Trophy,
  CheckCircle,
  XCircle,
  ChevronRight,
  Flame
} from 'lucide-react'
import gameService from '../../services/gameService'
import { renderContent } from '../../utils/contentRenderer'
import LoadingSpinner from '../../components/LoadingSpinner'
import { createLogger } from '../../utils/logger'

const logger = createLogger('ContextClues')

const TOTAL_ROUNDS = 10

const ContextClues = () => {
  const navigate = useNavigate()
  const [questions, setQuestions] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [score, setScore] = useState(0)
  const [streak, setStreak] = useState(0)
  const [bestStreak, setBestStreak] = useState(0)
  const [gameState, setGameState] = useState('loading')
  const [selectedOption, setSelectedOption] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const load = async () => {
      try {
        const qs = await gameService.getQuestions('Information and Ideas', TOTAL_ROUNDS)
        if (qs.length === 0) {
          setError('No reading comprehension questions available. Please try again later.')
          return
        }
        setQuestions(qs)
        setGameState('playing')
      } catch (err) {
        setError('Failed to load questions.')
        logger.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleSelect = useCallback(
    async (optionId) => {
      if (gameState !== 'playing' || selectedOption) return
      setSelectedOption(optionId)

      try {
        const result = await gameService.checkAnswer(questions[currentIndex].question_id, optionId)
        if (result.correct) {
          setScore((s) => s + 1)
          setStreak((s) => {
            const newStreak = s + 1
            setBestStreak((b) => Math.max(b, newStreak))
            return newStreak
          })
        } else {
          setStreak(0)
        }
        setFeedback({
          correct: result.correct,
          correctOptionId: result.correctOptionId,
          rationale: result.rationale,
          contentType: result.contentType
        })
      } catch {
        setFeedback({ correct: false, rationale: null })
        setStreak(0)
      }
      setGameState('feedback')
    },
    [gameState, selectedOption, currentIndex, questions]
  )

  const advance = useCallback(() => {
    if (currentIndex + 1 >= questions.length) {
      setGameState('completed')
    } else {
      setCurrentIndex((i) => i + 1)
      setSelectedOption(null)
      setFeedback(null)
      setGameState('playing')
    }
  }, [currentIndex, questions])

  const restart = useCallback(() => {
    setCurrentIndex(0)
    setScore(0)
    setStreak(0)
    setBestStreak(0)
    setSelectedOption(null)
    setFeedback(null)
    setGameState('loading')
    setLoading(true)
    gameService
      .getQuestions('Information and Ideas', TOTAL_ROUNDS)
      .then((qs) => {
        setQuestions(qs)
        setGameState('playing')
      })
      .catch(() => setError('Failed to reload.'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 py-8">
        <button
          onClick={() => navigate('/games')}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Practice Games
        </button>
        <div className="card card-body text-center py-10">
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    )
  }

  if (gameState === 'completed') {
    const accuracy = Math.round((score / questions.length) * 100)
    return (
      <div className="max-w-2xl mx-auto space-y-4 py-8">
        <button
          onClick={() => navigate('/games')}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Practice Games
        </button>
        <div className="card card-body text-center space-y-6 py-10">
          <Trophy className="w-16 h-16 text-yellow-500 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Session Complete!</h2>
          <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {score}/{questions.length}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Correct</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {accuracy}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Accuracy</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-orange-500">{bestStreak}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Best Streak</p>
            </div>
          </div>
          <button
            onClick={restart}
            className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            Play Again
          </button>
        </div>
      </div>
    )
  }

  const question = questions[currentIndex]

  return (
    <div className="max-w-2xl mx-auto space-y-4 py-8">
      <button
        onClick={() => navigate('/games')}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Practice Games
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-primary-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Question {currentIndex + 1}/{questions.length}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="font-medium text-green-600 dark:text-green-400">Score: {score}</span>
          {streak > 0 && (
            <span className="flex items-center gap-1 font-medium text-orange-500">
              <Flame className="w-4 h-4" />
              {streak}
            </span>
          )}
        </div>
      </div>

      {/* Question card */}
      <div className="card card-body space-y-4">
        {question.context && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {renderContent(question.context, question.content_type)}
          </div>
        )}

        <div className="text-gray-900 dark:text-gray-100">
          {renderContent(question.questionText, question.content_type)}
        </div>

        <div className="space-y-2">
          {question.answerOptions.map((opt) => {
            let optStyle =
              'border-gray-200 dark:border-gray-600 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
            if (gameState === 'feedback') {
              if (feedback?.correctOptionId === opt.option_id) {
                optStyle = 'border-green-500 bg-green-50 dark:bg-green-900/20'
              } else if (selectedOption === opt.option_id && !feedback?.correct) {
                optStyle = 'border-red-500 bg-red-50 dark:bg-red-900/20'
              } else {
                optStyle = 'border-gray-200 dark:border-gray-700 opacity-50'
              }
            }

            return (
              <button
                key={opt.option_id}
                onClick={() => handleSelect(opt.option_id)}
                disabled={gameState !== 'playing'}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all text-sm ${optStyle}`}
              >
                <div className="flex items-center gap-2">
                  {gameState === 'feedback' && feedback?.correctOptionId === opt.option_id && (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  )}
                  {gameState === 'feedback' &&
                    selectedOption === opt.option_id &&
                    !feedback?.correct && (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                  <span>{renderContent(opt.content, question.content_type)}</span>
                </div>
              </button>
            )
          })}
        </div>

        {feedback && (
          <div className="space-y-3">
            <div
              className={`text-sm font-medium py-2 px-3 rounded-lg ${
                feedback.correct
                  ? 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20'
                  : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20'
              }`}
            >
              {feedback.correct ? 'Correct!' : 'Incorrect'}
            </div>

            {feedback.rationale && (
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                <p className="text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase tracking-wide mb-2">
                  Explanation
                </p>
                <div className="text-sm text-primary-800 dark:text-primary-200 leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                  {renderContent(feedback.rationale, feedback.contentType || 'md')}
                </div>
              </div>
            )}

            <button
              onClick={advance}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next Question'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContextClues
